// cpuOpponent.js
// Play CPU — the local, in-memory engine for a single-player Pick Up Draft against
// the computer. There is NO Firestore and NO Cloud Function here: the whole game is a
// plain JavaScript object that we rebuild (immutably) as picks happen, and the same pure
// helpers the multiplayer screens use (from pickupService.js) read it unchanged.
//
// What lives here:
//   • DIFFICULTIES        — the three difficulty profiles + their pick probabilities
//   • buildCpuGame()      — constructs the initial game object (rounds, snake turn order,
//                           and the shared 5-card pools), mirroring how the real game is
//                           built server-side in pickUpMatchmaking.js
//   • applyPick()         — the local replacement for the processPickUpTurn Cloud Function:
//                           appends a pick, advances the turn/round, and finalizes the game
//   • cpuChooseMove()     — the CPU brain: ranks the available (slot, player) choices by the
//                           real team score (chemistry included) and picks one per difficulty
//   • resolveWinner()     — mirrors resolveWinner() in pickUpScoring.js exactly
//
// Everything the CPU "knows" is limited to its current roster and the players available
// right now — it never looks ahead to future rounds.

import { generateCandidates } from "./playerService";
import {
  calculateTeamRating,
  calculateTeamRatingWithChemistry,
} from "./teamRatingCalculator";
import {
  isRoundComplete,
  totalPicks,
  draftedPlayers,
  unfilledSlots,
  pool,
  takenPlayerNames,
  opponentID,
  isSlotFilled,
} from "./pickupService";

// The CPU's stand-in user id. Used everywhere a real opponent UID would go.
export const CPU_ID = "CPU";

// ─── Difficulty profiles ──────────────────────────────────────────────────────
// optimal + second + random always sum to 1. On each CPU turn we roll one random
// number and fall into exactly one band.
export const DIFFICULTIES = {
  normal: {
    id: "normal",
    label: "NORMAL",
    optimal: 0.75,
    second: 0.15,
    random: 0.1,
    blurb: "Makes the best move most of the time",
  },
  hard: {
    id: "hard",
    label: "HARD",
    optimal: 0.8,
    second: 0.15,
    random: 0.05,
    blurb: "Rarely slips up",
  },
  expert: {
    id: "expert",
    label: "EXPERT",
    optimal: 0.95,
    second: 0.05,
    random: 0,
    dropoffAware: true, // also weighs positional drop-off — see cpuChooseMove
    blurb: "Almost always optimal",
  },
};

// ─── Positions per sport ──────────────────────────────────────────────────────
// Mirrors SPORT_POSITIONS in pickUpMatchmaking.js (and Sport.pickUpPositions in Swift).
// Keyed by the web's lowercase sport id. This is the one piece of Pick Up structure the
// web didn't have yet — the multiplayer web reads `rounds` from the server-built document,
// but a local game has to build its own, so the source list lives here.
const SPORT_POSITIONS = {
  football: ["QB", "RB", "TE", "WR", "WR", "OL", "DL", "ED", "LB", "CB", "DB", "S"],
  baseball: ["C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "DH", "SP", "SP", "RP"],
  basketball: ["PG", "SG", "SF", "PF", "C", "G", "G", "F", "F", "C"],
  hockey: ["LW", "C", "RW", "LD", "RD", "G", "LW", "C", "RW", "LD", "RD"],
  soccer: ["GK", "DEF", "DEF", "DEF", "DEF", "MID", "MID", "MID", "W", "W", "ST"],
};

const RAW_FROM_ID = {
  football: "Football",
  baseball: "Baseball",
  basketball: "Basketball",
  hockey: "Hockey",
  soccer: "Soccer",
};

// ─── Structure builders (mirror pickUpMatchmaking.js) ─────────────────────────

// Groups positions into rounds of 3 (last round may be smaller). Matches buildRounds().
function buildRounds(positions) {
  const rounds = [];
  for (let i = 0; i < positions.length; i += 3) {
    rounds.push(positions.slice(i, i + 3));
  }
  return rounds;
}

// Snake order as "A"/"B" placeholders: A, B, B, A, A, B, B … Matches buildSnakeTurnOrder().
function buildSnakeTurnOrder(picks) {
  const order = [];
  let nextFirst = "A";
  let remaining = picks;
  while (remaining > 0) {
    const first = nextFirst;
    const second = first === "A" ? "B" : "A";
    order.push(first);
    remaining--;
    if (remaining > 0) {
      order.push(second);
      remaining--;
    }
    nextFirst = nextFirst === "A" ? "B" : "A";
  }
  return order;
}

// Builds the shared 5-card pool for every slot, keyed "roundIndex" → "position_slot" → [players].
// `used` threads across every slot so a player never appears in two different pools — the same
// de-dupe the server's generatePlayerPools() does. Reuses playerService.generateCandidates so
// the pools match the rest of the web app's drafts exactly.
function buildPlayerPools(sportId, rounds, allPlayers) {
  const used = new Set();
  const pools = {};
  rounds.forEach((positions, roundIndex) => {
    const roundPools = {};
    positions.forEach((position, slotIndex) => {
      const candidates = generateCandidates(allPlayers, sportId, position, used);
      candidates.forEach((p) => used.add(p.name));
      roundPools[`${position}_${slotIndex}`] = candidates;
    });
    pools[String(roundIndex)] = roundPools;
  });
  return pools;
}

// ─── Game construction ────────────────────────────────────────────────────────

/**
 * Builds a ready-to-play local game object. The human is always Player A (picks first);
 * the CPU is Player B. The returned object has the exact same shape the pickupService
 * helpers and the PickUpComplete screen expect, so both read it with no changes.
 */
export function buildCpuGame({ sportId, difficulty, humanUID, humanUsername, allPlayers }) {
  const positions = SPORT_POSITIONS[sportId];
  if (!positions) throw new Error(`Unknown sport: ${sportId}`);

  const diff = DIFFICULTIES[difficulty] || DIFFICULTIES.normal;
  const rounds = buildRounds(positions);
  const template = buildSnakeTurnOrder(positions.length * 2); // each position drafted by both
  const playerPools = buildPlayerPools(sportId, rounds, allPlayers);
  const now = new Date();

  // Randomly decide who picks first. Whoever is "Player A" leads the snake order (A, B, B, A …),
  // so we assign the human to A or B on a coin flip. Everything else in the game keys off the
  // user's uid rather than their A/B slot, so it works the same whichever way the flip lands.
  const humanFirst = Math.random() < 0.5;
  const human = { id: humanUID, username: humanUsername || "YOU" };
  const cpu = { id: CPU_ID, username: `CPU · ${diff.label}` };
  const playerA = humanFirst ? human : cpu;
  const playerB = humanFirst ? cpu : human;
  const turnOrder = template.map((slot) => (slot === "A" ? playerA.id : playerB.id));

  return {
    id: `cpu_${now.getTime()}`,
    playerAID: playerA.id,
    playerBID: playerB.id,
    playerAUsername: playerA.username,
    playerBUsername: playerB.username,
    sport: sportId,
    sportRaw: RAW_FROM_ID[sportId],
    status: "active",
    turnOrder,
    currentPickIndex: 0,
    currentRoundIndex: 0,
    rounds,
    playerPools,
    picks: [],
    missedPickCounts: {},
    firstPickerID: playerA.id,
    createdAt: now,
    lastActionAt: now,
    turnExpiresAt: null, // solo games have no timer
    result: null,
    viewedBy: [],
    difficulty: diff.id,
  };
}

// ─── Applying a pick (local replacement for processPickUpTurn) ─────────────────

/**
 * Returns a NEW game object with the given pick applied: the pick is appended, the turn
 * advances, the round advances once both players have filled it, and the game finalizes
 * (status "complete" + result) after the last pick. Pure — never mutates the input.
 */
export function applyPick(game, { userID, roundIndex, position, slotInRound, player, isAutoPick = false }) {
  const pickIndex = game.picks.length;
  const newPick = {
    id: `${userID}_${pickIndex}`,
    userID,
    position,
    player,
    pickIndex,
    roundIndex,
    slotInRound,
    timestamp: new Date(),
    isAutoPick,
  };

  const next = {
    ...game,
    picks: [...game.picks, newPick],
    currentPickIndex: game.currentPickIndex + 1,
    lastActionAt: new Date(),
  };

  // Advance to the next round once both players have completed the current one.
  if (
    next.currentRoundIndex < next.rounds.length - 1 &&
    isRoundComplete(next, next.currentRoundIndex)
  ) {
    next.currentRoundIndex += 1;
  }

  // Finalize once every pick is in.
  if (next.picks.length >= totalPicks(next)) {
    next.status = "complete";
    next.result = resolveWinner(next);
  }

  return next;
}

// ─── Winner resolution (mirrors resolveWinner in pickUpScoring.js) ─────────────

// Chemistry points = rating-with-chemistry minus base rating. Matches calculateChemistryPoints().
function chemistryPoints(players) {
  return calculateTeamRatingWithChemistry(players) - calculateTeamRating(players);
}

/**
 * Decides the winner of a finished game. Tiebreak order: higher rating, then higher
 * chemistry, then the first picker (draft-order advantage). Same logic and field shape as
 * the server so the PickUpComplete screen reads result.winnerID / result.tiebreaker as usual.
 */
export function resolveWinner(game) {
  const aPlayers = draftedPlayers(game, game.playerAID);
  const bPlayers = draftedPlayers(game, game.playerBID);

  const ratingA = calculateTeamRatingWithChemistry(aPlayers);
  const ratingB = calculateTeamRatingWithChemistry(bPlayers);
  const chemA = chemistryPoints(aPlayers);
  const chemB = chemistryPoints(bPlayers);

  let winnerID;
  let tiebreaker = null;
  if (ratingA !== ratingB) {
    winnerID = ratingA > ratingB ? game.playerAID : game.playerBID;
  } else if (chemA !== chemB) {
    winnerID = chemA > chemB ? game.playerAID : game.playerBID;
    tiebreaker = "chemistry";
  } else {
    winnerID = game.firstPickerID;
    tiebreaker = "draft_order";
  }

  const loserID = winnerID === game.playerAID ? game.playerBID : game.playerAID;
  return {
    winnerID,
    winnerRating: winnerID === game.playerAID ? ratingA : ratingB,
    winnerChemistry: winnerID === game.playerAID ? chemA : chemB,
    loserID,
    loserRating: loserID === game.playerAID ? ratingA : ratingB,
    loserChemistry: loserID === game.playerAID ? chemA : chemB,
    tiebreaker,
    resolvedAt: new Date(),
  };
}

// ─── The CPU brain ─────────────────────────────────────────────────────────────

// Every (slot, player) choice open to the CPU this turn, each scored by the team rating
// (chemistry included) it would produce if added to the CPU's current roster.
//
// Position priority: each slot's 5-card pool is shared with the user, so once the user has
// filled a slot they can no longer draft from that pool — its leftover players are "safe"
// (only the CPU can still take them, so they'll still be there later). A slot the user hasn't
// filled yet is "contested": if the CPU waits, the user might grab the best player first.
// So the CPU only considers contested slots, and falls back to safe slots only when there are
// no contested ones left this turn. This is why, e.g., the CPU won't answer the user's QB with
// its own QB pick — it grabs at-risk positions first and saves the safe QB for last.
function scoredChoices(game) {
  const roster = draftedPlayers(game, CPU_ID);
  const roundIndex = game.currentRoundIndex;
  const taken = takenPlayerNames(game, roundIndex);
  const humanID = opponentID(game, CPU_ID);

  const openSlots = unfilledSlots(game, CPU_ID, roundIndex);
  const contested = openSlots.filter(
    (s) => !isSlotFilled(game, humanID, roundIndex, s.slotInRound)
  );
  const prioritized = contested.length > 0 ? contested : openSlots;

  const choices = [];
  prioritized.forEach(({ position, slotInRound }) => {
    const available = pool(game, roundIndex, position, slotInRound).filter(
      (p) => !taken.has(p.name)
    );
    available.forEach((player) => {
      const score = calculateTeamRatingWithChemistry([...roster, player]);
      choices.push({ position, slotInRound, player, score });
    });
  });

  return choices;
}

/**
 * Picks the CPU's move for this turn. Rolls one random number and, per the difficulty
 * profile, takes the best choice, the second-best, or a fully random legal choice.
 * Returns { position, slotInRound, player } or null if there's nothing to pick.
 *
 * "Best" maximizes the CPU's real team score with chemistry, considering only its current
 * roster and the players available right now — it does not look ahead to later rounds.
 * On Expert, "best" also weighs positional drop-off (see rankExpertMoves): among positions
 * of similar quality it favors the one whose top player is far better than its alternatives,
 * since that's the pick it can least afford to lose to the user by waiting.
 */
export function cpuChooseMove(game, difficultyId) {
  const diff = DIFFICULTIES[difficultyId] || DIFFICULTIES.normal;
  const choices = scoredChoices(game);
  if (choices.length === 0) return null;

  const ranked = diff.dropoffAware ? rankExpertMoves(choices) : rankByScore(choices);

  const roll = Math.random();
  let choice;
  if (roll < diff.optimal) {
    choice = ranked[0];
  } else if (roll < diff.optimal + diff.second) {
    choice = ranked[1] || ranked[0]; // fall back to best if only one option exists
  } else {
    // Random: a legal player at a slot that still needs filling.
    choice = ranked[Math.floor(Math.random() * ranked.length)];
  }

  return { position: choice.position, slotInRound: choice.slotInRound, player: choice.player };
}

// Normal / Hard ranking: every available player, best resulting team score first. Ties break by
// the player's own rating, then randomly so the CPU isn't deterministic on exact ties.
function rankByScore(choices) {
  return [...choices].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (b.player.overallRating !== a.player.overallRating) {
      return b.player.overallRating - a.player.overallRating;
    }
    return Math.random() - 0.5;
  });
}

// Expert ranking: one entry per position (its best player), ordered by drop-off urgency.
// For each position we measure how much better its best option is than its NEXT option
// (best − second-best). Priority = best score + that drop-off, so a position whose top player
// towers over the rest (e.g. one elite RB at 99 with the next at 81) outranks a position where
// every option is similar (e.g. QBs a point apart) — because deferring the steep one risks
// losing the most value to the user, while the shallow one can safely wait. Weighting the best
// score directly (not just the gap) keeps a genuinely elite player ahead of a weak position
// that merely happens to have a big internal drop-off.
function rankExpertMoves(choices) {
  const bySlot = new Map();
  for (const c of choices) {
    const key = `${c.position}_${c.slotInRound}`;
    if (!bySlot.has(key)) bySlot.set(key, []);
    bySlot.get(key).push(c);
  }

  const perSlot = [];
  for (const group of bySlot.values()) {
    const sorted = group.sort((a, b) => b.score - a.score);
    const best = sorted[0];
    const secondScore = sorted.length > 1 ? sorted[1].score : sorted[0].score;
    const dropoff = best.score - secondScore;
    perSlot.push({ ...best, priority: best.score + dropoff });
  }

  return perSlot.sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return b.player.overallRating - a.player.overallRating;
  });
}
